const GRAVITY_MPS2 = 9.80665;

export const SOFT_BRAKING_G = 0.12;
export const HARD_BRAKING_G = 0.45;
export const COASTING_MIN_G = 0.025;

export type BrakingSeverity = 'soft' | 'hard';

export type BrakingTelemetryPoint = {
  timecodeMs: number;
  lat: number;
  lon: number;
  speedKph: number;
};

export type BrakingZone<T extends BrakingTelemetryPoint = BrakingTelemetryPoint> = {
  id: string;
  severity: BrakingSeverity;
  startTimecodeMs: number;
  endTimecodeMs: number;
  peakDecelerationG: number;
  peakPoint: T;
  points: T[];
};

export type CoastingZone<T extends BrakingTelemetryPoint = BrakingTelemetryPoint> = {
  id: string;
  startTimecodeMs: number;
  endTimecodeMs: number;
  peakDecelerationG: number;
  peakPoint: T;
  points: T[];
};

/** Produces a rounded map arc without moving the path outside its GPS segments. */
export function smoothTelemetryArc(
  points: Array<{ lat: number; lon: number }>,
  iterations = 2,
): Array<{ lat: number; lon: number }> {
  if (points.length < 3 || iterations <= 0)
    return points.map((point) => ({ lat: point.lat, lon: point.lon }));

  let arc = points.map((point) => ({ lat: point.lat, lon: point.lon }));
  for (let iteration = 0; iteration < iterations; iteration += 1) {
    const next = [arc[0]!];
    for (let index = 0; index < arc.length - 1; index += 1) {
      const start = arc[index]!;
      const end = arc[index + 1]!;
      next.push(
        { lat: start.lat * 0.75 + end.lat * 0.25, lon: start.lon * 0.75 + end.lon * 0.25 },
        { lat: start.lat * 0.25 + end.lat * 0.75, lon: start.lon * 0.25 + end.lon * 0.75 },
      );
    }
    next.push(arc.at(-1)!);
    arc = next;
  }
  return arc;
}

type ClassifiedPoint<T extends BrakingTelemetryPoint> = {
  point: T;
  index: number;
  decelerationG: number;
  severity: BrakingSeverity;
};

function smoothedSpeeds<T extends BrakingTelemetryPoint>(points: T[], radiusMs: number) {
  const result: number[] = [];
  let left = 0;
  let right = 0;
  let sum = 0;

  for (let index = 0; index < points.length; index += 1) {
    const time = points[index]!.timecodeMs;
    while (right < points.length && points[right]!.timecodeMs <= time + radiusMs) {
      sum += points[right]!.speedKph;
      right += 1;
    }
    while (left < right && points[left]!.timecodeMs < time - radiusMs) {
      sum -= points[left]!.speedKph;
      left += 1;
    }
    result.push(sum / Math.max(1, right - left));
  }

  return result;
}

function distanceMeters(left: { lat: number; lon: number }, right: { lat: number; lon: number }) {
  const latitudeScale = 111_320;
  const meanLatitudeRadians = (((left.lat + right.lat) / 2) * Math.PI) / 180;
  const latitudeMeters = (right.lat - left.lat) * latitudeScale;
  const longitudeMeters = (right.lon - left.lon) * latitudeScale * Math.cos(meanLatitudeRadians);
  return Math.hypot(latitudeMeters, longitudeMeters);
}

export function isContinuousTelemetryPair(
  left: BrakingTelemetryPoint,
  right: BrakingTelemetryPoint,
) {
  const elapsedMs = right.timecodeMs - left.timecodeMs;
  if (elapsedMs <= 0 || elapsedMs > 700) return false;
  const expectedMeters = (Math.max(left.speedKph, right.speedKph, 8) / 3.6) * (elapsedMs / 1000);
  return distanceMeters(left, right) <= Math.max(6, expectedMeters * 2 + 4);
}

/** Splits telemetry into drawable runs without bridging missing or implausible GPS samples. */
export function splitContinuousTelemetryPoints<T extends BrakingTelemetryPoint>(
  points: T[],
): T[][] {
  const runs: T[][] = [];

  for (const point of points) {
    const current = runs.at(-1);
    const previous = current?.at(-1);
    if (!current || !previous || !isContinuousTelemetryPair(previous, point)) {
      runs.push([point]);
    } else {
      current.push(point);
    }
  }

  return runs.filter((run) => run.length >= 2);
}

function contiguousZonePoints<T extends BrakingTelemetryPoint>(
  points: T[],
  startIndex: number,
  endIndex: number,
  peakIndex: number,
) {
  let left = peakIndex;
  let right = peakIndex;
  while (left > startIndex) {
    const current = points[left]!;
    const previous = points[left - 1]!;
    if (!isContinuousTelemetryPair(previous, current)) break;
    left -= 1;
  }
  while (right < endIndex) {
    const current = points[right]!;
    const next = points[right + 1]!;
    if (!isContinuousTelemetryPair(current, next)) break;
    right += 1;
  }
  return points.slice(left, right + 1);
}

/**
 * Finds sustained braking zones from GPS speed. Deceleration is calculated over
 * a centered, smoothed window to avoid turning individual noisy GPS samples
 * into false brake markers. Positive `peakDecelerationG` means braking force.
 */
export function analyzeBrakingZones<T extends BrakingTelemetryPoint>(input: T[]): BrakingZone<T>[] {
  const points = input
    .filter(
      (point) =>
        Number.isFinite(point.timecodeMs) &&
        Number.isFinite(point.speedKph) &&
        Number.isFinite(point.lat) &&
        Number.isFinite(point.lon),
    )
    .sort((left, right) => left.timecodeMs - right.timecodeMs);
  if (points.length < 5) return [];

  const speeds = smoothedSpeeds(points, 160);
  const classified: ClassifiedPoint<T>[] = [];
  let before = 0;
  let after = 1;

  for (let index = 1; index < points.length - 1; index += 1) {
    const targetBefore = points[index]!.timecodeMs - 220;
    const targetAfter = points[index]!.timecodeMs + 220;
    while (before + 1 < index && points[before + 1]!.timecodeMs <= targetBefore) before += 1;
    after = Math.max(after, index + 1);
    while (after < points.length - 1 && points[after]!.timecodeMs < targetAfter) after += 1;

    const durationSec = (points[after]!.timecodeMs - points[before]!.timecodeMs) / 1000;
    if (durationSec < 0.18 || durationSec > 1.2 || speeds[index]! < 8) continue;

    const accelerationMps2 = (speeds[after]! - speeds[before]!) / 3.6 / durationSec;
    const decelerationG = -accelerationMps2 / GRAVITY_MPS2;
    if (decelerationG < SOFT_BRAKING_G) continue;

    classified.push({
      point: points[index]!,
      index,
      decelerationG,
      severity: decelerationG >= HARD_BRAKING_G ? 'hard' : 'soft',
    });
  }

  const groups: ClassifiedPoint<T>[][] = [];
  for (const sample of classified) {
    const current = groups.at(-1);
    const previous = current?.at(-1);
    if (
      !current ||
      !previous ||
      sample.point.timecodeMs - previous.point.timecodeMs > 320 ||
      !isContinuousTelemetryPair(previous.point, sample.point)
    ) {
      groups.push([sample]);
    } else {
      current.push(sample);
    }
  }

  return groups.flatMap((group, groupIndex) => {
    const first = group[0]!;
    const last = group.at(-1)!;
    const peak = group.reduce((best, sample) =>
      sample.decelerationG > best.decelerationG ? sample : best,
    );
    const durationMs = last.point.timecodeMs - first.point.timecodeMs;
    if (durationMs < 140 && peak.decelerationG < HARD_BRAKING_G) return [];

    const startIndex = Math.max(0, first.index - 1);
    const endIndex = Math.min(points.length - 1, last.index + 1);
    const zonePoints = contiguousZonePoints(points, startIndex, endIndex, peak.index);
    if (zonePoints.length < 2) return [];
    const severity: BrakingSeverity = group.some((sample) => sample.severity === 'hard')
      ? 'hard'
      : 'soft';
    return [
      {
        id: `braking-${severity}-${groupIndex}-${first.point.timecodeMs}`,
        severity,
        startTimecodeMs: zonePoints[0]!.timecodeMs,
        endTimecodeMs: zonePoints.at(-1)!.timecodeMs,
        peakDecelerationG: peak.decelerationG,
        peakPoint: peak.point,
        points: zonePoints,
      },
    ];
  });
}

/**
 * Finds sustained mild deceleration that is below the soft-braking threshold.
 * This is an estimate from GPS speed, not proof that the throttle is closed.
 */
export function analyzeCoastingZones<T extends BrakingTelemetryPoint>(
  input: T[],
): CoastingZone<T>[] {
  const points = input
    .filter(
      (point) =>
        Number.isFinite(point.timecodeMs) &&
        Number.isFinite(point.speedKph) &&
        Number.isFinite(point.lat) &&
        Number.isFinite(point.lon),
    )
    .sort((left, right) => left.timecodeMs - right.timecodeMs);
  if (points.length < 5) return [];

  const speeds = smoothedSpeeds(points, 160);
  const classified: Array<{ point: T; index: number; decelerationG: number }> = [];
  let before = 0;
  let after = 1;

  for (let index = 1; index < points.length - 1; index += 1) {
    const targetBefore = points[index]!.timecodeMs - 220;
    const targetAfter = points[index]!.timecodeMs + 220;
    while (before + 1 < index && points[before + 1]!.timecodeMs <= targetBefore) before += 1;
    after = Math.max(after, index + 1);
    while (after < points.length - 1 && points[after]!.timecodeMs < targetAfter) after += 1;

    const durationSec = (points[after]!.timecodeMs - points[before]!.timecodeMs) / 1000;
    if (durationSec < 0.18 || durationSec > 1.2 || speeds[index]! < 8) continue;

    const accelerationMps2 = (speeds[after]! - speeds[before]!) / 3.6 / durationSec;
    const decelerationG = -accelerationMps2 / GRAVITY_MPS2;
    if (decelerationG < COASTING_MIN_G || decelerationG >= SOFT_BRAKING_G) continue;

    classified.push({ point: points[index]!, index, decelerationG });
  }

  const groups: (typeof classified)[] = [];
  for (const sample of classified) {
    const current = groups.at(-1);
    const previous = current?.at(-1);
    if (
      !current ||
      !previous ||
      sample.point.timecodeMs - previous.point.timecodeMs > 320 ||
      !isContinuousTelemetryPair(previous.point, sample.point)
    ) {
      groups.push([sample]);
    } else {
      current.push(sample);
    }
  }

  return groups.flatMap((group, groupIndex) => {
    const first = group[0]!;
    const last = group.at(-1)!;
    if (last.point.timecodeMs - first.point.timecodeMs < 300) return [];

    const peak = group.reduce((best, sample) =>
      sample.decelerationG > best.decelerationG ? sample : best,
    );
    const startIndex = Math.max(0, first.index - 1);
    const endIndex = Math.min(points.length - 1, last.index + 1);
    const zonePoints = contiguousZonePoints(points, startIndex, endIndex, peak.index);
    if (zonePoints.length < 2) return [];
    return [
      {
        id: `coasting-${groupIndex}-${first.point.timecodeMs}`,
        startTimecodeMs: zonePoints[0]!.timecodeMs,
        endTimecodeMs: zonePoints.at(-1)!.timecodeMs,
        peakDecelerationG: peak.decelerationG,
        peakPoint: peak.point,
        points: zonePoints,
      },
    ];
  });
}
